import React, { useState, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  ContentCopy as CopyIcon,
  MoreVert as MoreVertIcon,
  FileCopy as FileCopyIcon
} from '@mui/icons-material';

import MemoizedAutocomplete from './MemoizedAutocomplete';

const MilestoneSection = ({
  milestone,
  expanded,
  onToggleExpansion,
  onRoleChange,
  onAddRole,
  onEditRole,
  onEditTimeline,
  onRemoveRole,
  onMoveRole,
  onCopyRole,
  onCopyAllRoles,
  calculateMilestoneTotals,
  getMemberOverAllocation,
  renderRateWithIndicator,
  formatCurrency,
  itRoster,
  locationOptions,
  roleOptions,
  readOnly,
  saving,
  deptFilter,
  departments,
  onUpdateMilestoneDepts
}) => {
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [milestoneMenuAnchor, setMilestoneMenuAnchor] = useState(null);
  const [deptTagAnchor, setDeptTagAnchor] = useState(null);

  const milestoneTotals = calculateMilestoneTotals(milestone);

  // Compute filtered role sets at component level so both header and table can use them
  const matchingRoles = deptFilter
    ? (milestone.roles || []).filter(r => !r.department || r.department === deptFilter)
    : (milestone.roles || []);
  const otherRoles = deptFilter
    ? (milestone.roles || []).filter(r => r.department && r.department !== deptFilter)
    : [];

  const handleDeptTagToggle = (deptName) => {
    const current = milestone.departments || [];
    const updated = current.includes(deptName)
      ? current.filter(d => d !== deptName)
      : [...current, deptName];
    if (onUpdateMilestoneDepts) onUpdateMilestoneDepts(milestone.id, updated);
  };

  const handleRoleMenuClick = useCallback((event, role) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    setRoleMenuAnchor(event.currentTarget);
    setSelectedRole(role);
  }, []);

  const handleRoleMenuClose = useCallback(() => {
    setRoleMenuAnchor(null);
    setSelectedRole(null);
  }, []);

  const handleCopyRoleClick = useCallback(() => {
    if (selectedRole && onCopyRole) {
      onCopyRole(selectedRole);
    }
    handleRoleMenuClose();
  }, [selectedRole, onCopyRole, handleRoleMenuClose]);

  const handleMilestoneMenuClick = useCallback((event) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    setMilestoneMenuAnchor(event.currentTarget);
  }, []);

  const handleMilestoneMenuClose = useCallback(() => {
    setMilestoneMenuAnchor(null);
  }, []);

  const handleCopyAllRolesClick = useCallback((event) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    if (onCopyAllRoles) {
      onCopyAllRoles(event, milestone);
    }
    handleMilestoneMenuClose();
  }, [milestone, onCopyAllRoles, handleMilestoneMenuClose]);

  return (
    <Accordion expanded={expanded} onChange={onToggleExpansion} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {milestone.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {milestone.start_date} to {milestone.end_date} •{' '}
              {deptFilter && otherRoles.length > 0
                ? `${matchingRoles.length}/${milestone.roles.length} roles`
                : `${milestone.roles?.length || 0} roles`}
            </Typography>
            {/* Milestone department tags */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }} onClick={e => e.stopPropagation()}>
              {(milestone.departments || []).map(dept => {
                const deptLabel = typeof dept === 'object' ? dept.name : dept;
                return (
                  <Chip
                    key={deptLabel}
                    label={deptLabel}
                    size="small"
                    color="info"
                    variant="outlined"
                    onDelete={readOnly ? undefined : () => handleDeptTagToggle(deptLabel)}
                    sx={{ fontSize: '0.65rem', height: 18 }}
                  />
                );
              })}
              {!readOnly && (
                <Chip
                  size="small"
                  label="+ dept"
                  variant="outlined"
                  onClick={e => { e.stopPropagation(); setDeptTagAnchor(e.currentTarget); }}
                  sx={{ fontSize: '0.65rem', height: 18, cursor: 'pointer' }}
                />
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(milestoneTotals.total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                C: {formatCurrency(milestoneTotals.capex)} | O: {formatCurrency(milestoneTotals.opex)}
              </Typography>
            </Box>
            
            <Chip
              label={milestone.capex_classification || 'OPEX'}
              size="small"
              color={milestone.capex_classification === 'CAPEX' ? 'primary' : 'default'}
              variant="outlined"
            />
            
            {/* Milestone Actions Menu */}
            {milestone.roles && milestone.roles.length > 0 && (
              <IconButton
                size="small"
                onClick={handleMilestoneMenuClick}
                disabled={readOnly}
                sx={{ ml: 1 }}
              >
                <MoreVertIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      
      <AccordionDetails>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Roles ({deptFilter && otherRoles.length > 0
              ? `${matchingRoles.length}/${milestone.roles?.length || 0}`
              : milestone.roles?.length || 0})
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {milestone.roles && milestone.roles.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<FileCopyIcon />}
                onClick={handleCopyAllRolesClick}
                disabled={readOnly || saving}
                size="small"
                color="secondary"
                title={`Copy all ${milestone.roles.length} roles to another milestone`}
              >
                Copy All ({milestone.roles.length})
              </Button>
            )}
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => onAddRole(milestone)}
              disabled={readOnly || saving}
              size="small"
            >
              Add Role
            </Button>
          </Box>
        </Box>

        {milestone.roles && milestone.roles.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Timeline</TableCell>
                  <TableCell>Allocation</TableCell>
                  <TableCell>Classification</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matchingRoles.map((role, index) => {
                  const monthlyRate = role.rate || 0;
                  const allocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
                  const months = Math.max(1, role.overlap_months || 1);
                  const roleCost = monthlyRate * allocation * months;

                  const memberOverAllocation = role.roster_member_id ?
                    getMemberOverAllocation(role.roster_member_id) : null;

                  return (
                    <TableRow key={role.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {role.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {role.function}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <MemoizedAutocomplete
                          role={role}
                          itRoster={itRoster}
                          onRosterMemberChange={(newValue) => 
                            onRoleChange(milestone.id, role.id, 'roster_member_id', newValue?.id || '')
                          }
                          readOnly={readOnly}
                        />
                        {memberOverAllocation?.hasOverAllocation && (
                          <Tooltip 
                            title={`Over-allocated: ${memberOverAllocation.maxAllocation}%`}
                            arrow
                          >
                            <Chip
                              label={`${memberOverAllocation.maxAllocation}%`}
                              size="small"
                              color="error"
                              sx={{ mt: 0.5, fontSize: '0.6rem' }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip
                              label={role.company || 'CA'}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 16 }}
                            />
                            <Chip
                              label={role.location || 'US'}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 16 }}
                            />
                            {role.department && (
                              <Chip
                                label={typeof role.department === 'object' ? role.department.name : role.department}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 16, borderColor: 'warning.main', color: 'warning.dark' }}
                              />
                            )}
                          </Box>
                          {role.role && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                              {role.role.length > 20 ? `${role.role.substring(0, 20)}...` : role.role}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        {renderRateWithIndicator(role)}
                      </TableCell>
                      
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<TimelineIcon />}
                          onClick={() => onEditTimeline(role)}
                          disabled={readOnly}
                          sx={{ fontSize: '0.6rem', py: 0.25, px: 0.5 }}
                        >
                          Timeline
                        </Button>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {role.allocation || 0}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({months}mo)
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Chip
                          label={role.capex_classification || 'OPEX'}
                          size="small"
                          color={role.capex_classification === 'CAPEX' ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.6rem' }}
                        />
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(roleCost)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => onMoveRole && onMoveRole(milestone.id, role.id, 'up')}
                            disabled={readOnly || index === 0}
                          >
                            <MoveUpIcon sx={{ fontSize: '0.8rem' }} />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            onClick={() => onMoveRole && onMoveRole(milestone.id, role.id, 'down')}
                            disabled={readOnly || index === milestone.roles.length - 1}
                          >
                            <MoveDownIcon sx={{ fontSize: '0.8rem' }} />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            onClick={() => onEditRole(role)}
                            disabled={readOnly}
                          >
                            <EditIcon sx={{ fontSize: '0.8rem' }} />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            onClick={(e) => handleRoleMenuClick(e, role)}
                            disabled={readOnly}
                          >
                            <MoreVertIcon sx={{ fontSize: '0.8rem' }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
            <AddIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">
              No roles added yet
            </Typography>
            <Typography variant="caption">
              Add team members to this milestone
            </Typography>
          </Box>
        )}
      </AccordionDetails>

      {/* Role Actions Menu */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={Boolean(roleMenuAnchor)}
        onClose={handleRoleMenuClose}
      >
        <MenuItem onClick={handleCopyRoleClick}>
          <CopyIcon sx={{ mr: 1, fontSize: '0.8rem' }} />
          Copy Role
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedRole && onRemoveRole) {
              onRemoveRole(milestone.id, selectedRole.id);
            }
            handleRoleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: '0.8rem' }} />
          Remove Role
        </MenuItem>
      </Menu>

      {/* Milestone Actions Menu */}
      <Menu
        anchorEl={milestoneMenuAnchor}
        open={Boolean(milestoneMenuAnchor)}
        onClose={handleMilestoneMenuClose}
      >
        <MenuItem onClick={handleCopyAllRolesClick}>
          <FileCopyIcon sx={{ mr: 1, fontSize: '0.8rem' }} />
          Copy All Roles ({milestone.roles?.length || 0})
        </MenuItem>
      </Menu>

      {/* Milestone dept tag popover */}
      <Popover
        open={Boolean(deptTagAnchor)}
        anchorEl={deptTagAnchor}
        onClose={() => setDeptTagAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, minWidth: 180 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            Tag milestone departments
          </Typography>
          <List dense disablePadding>
            {(departments || []).map(d => {
              const checked = (milestone.departments || []).includes(d.name);
              return (
                <ListItem key={d.id} disablePadding>
                  <ListItemButton onClick={() => handleDeptTagToggle(d.name)} dense>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple size="small" />
                    </ListItemIcon>
                    <ListItemText primary={d.name} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {(!departments || departments.length === 0) && (
              <ListItem>
                <ListItemText primary="No departments configured" primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} />
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>
    </Accordion>
  );
};

export default MilestoneSection;